import { useCallback, useEffect, useState, useContext } from 'react';
import { useParams } from 'react-router-dom';
import { Table, Row, Col, Spin } from 'antd';
import { Icon } from '@fluentui/react/lib/Icon';
import moment from 'moment';

import FormSectionTitle from '../../../../../../../components/FormSectionTitle/FormSectionTitle';
import PADViewModal from '../../modals/PADViewModal/PADViewModal';

import { BankingContext } from '../../BankingContext';
import { BankingPageViewEnum } from '../../types';
import { convertDateToReadable } from '../../utils';

import useModal from '../../../../../../../hooks/useModal';
import useLocale from '../../../../../../../hooks/useLocale';

import { DATE_AND_TIME_FORMAT } from '../../../../../../../constants/common';
import { ClientService } from '../../../../../../../shared/api/ClientService';
import API from '../../../../../../../utils/api';

import './PADTable.scss';
import styles from '../../../../../../../styles/style.module.scss';

const PADTable = () => {
  const { applicationFileId } = useParams<{ applicationFileId?: string }>();
  const { showModal, closeModal } = useModal();
  const { setPageView } = useContext(BankingContext);
  const { t } = useLocale();

  const [dataSource, setDataSource] = useState<ClientService.PADInfoChangeRequestDto[]>();
  const [loading, setLoading] = useState<boolean>(false);

  const requestGetPADList = useCallback(async (applicationFileId: string) => {
    setLoading(true);
    const response = await API.padInfoChangeRequestAll(applicationFileId).catch(() => setLoading(false));
    setLoading(false);

    if (response) setDataSource(response);
  }, []);

  const handlePADView = useCallback(
    (id: string) => {
      showModal(<PADViewModal onClose={closeModal} applicationFileId={applicationFileId as string} id={id} />);
    },
    [applicationFileId, closeModal, showModal]
  );

  useEffect(() => {
    if (applicationFileId) requestGetPADList(applicationFileId);
  }, [applicationFileId, requestGetPADList]);

  const columns = [
    {
      title: t.UPDATED_BY,
      dataIndex: 'updatedBy',
      key: 'updatedBy',
    },
    {
      title: t.UPDATED_ON,
      dataIndex: 'updateOn',
      key: 'updateOn',
      render: (date: moment.Moment) => <>{convertDateToReadable(date, DATE_AND_TIME_FORMAT)}</>,
    },
    {
      title: t.REQUESTED_BY,
      dataIndex: 'requestedBy',
      key: 'requestedBy',
    },
    {
      title: t.REQUESTED_ON,
      dataIndex: 'requestedOn',
      key: 'requestedOn',
      render: (date: moment.Moment) => <>{convertDateToReadable(date, DATE_AND_TIME_FORMAT)}</>,
    },
    {
      dataIndex: 'action',
      key: 'action',
      render: (_: any, record: ClientService.PADInfoChangeRequestDto) => (
        <Icon
          iconName="DrillDown"
          style={{ fontSize: 17, color: styles.colorDanger, transform: `rotate(-90deg)`, cursor: 'pointer' }}
          onClick={() => handlePADView(record?.id as string)}
        />
      ),
    },
  ];

  return (
    <div className="PADTable">
      <Row align="middle" justify="space-between">
        <Col>
          <FormSectionTitle titleText={t.PAD_HISTORY} />
        </Col>
        <Col>
          <Row
            onClick={() => setPageView(BankingPageViewEnum.Banking)}
            style={{ color: styles.colorDanger, cursor: 'pointer' }}
            gutter={10}
            align="middle"
            justify="end"
          >
            <Col>{t.BANKING}</Col>
            <Col>
              <Icon iconName="RevToggleKey" style={{ fontSize: 15 }} />
            </Col>
          </Row>
        </Col>
      </Row>

      <Spin spinning={loading}>
        <Table dataSource={dataSource} columns={columns} />
      </Spin>
    </div>
  );
};

export default PADTable;
